import { apiClient } from './api-client';
import { Booking, APIResponse, ContactInfo } from '@/types';
import { BookingConfirmationService } from './booking-confirmation-service';

export interface BookingForm {
  listingId: string;
  guestId: string;
  guestName: string; // Added required guest name field
  guestEmail: string; // Added required guest email field
  startDate: string;
  endDate: string;
  guests: number;
  totalPrice: number;
  useCustomPricing: boolean; // Flag to indicate if using custom pricing
  customPriceBreakdown?: {
    basePrice: number;
    cleaningFee?: number;
    serviceFee?: number;
    taxes?: number;
    discounts?: number;
    notes?: string;
  };
  // Additional contact information (optional)
  primaryGuestContact: {
    whatsappNumber?: string;
    preferredMethod: 'email' | 'whatsapp' | 'both';
  };
  expectedGuests: number;
}

export class BookingsService {
  // Get all bookings for the current user (as host)
  static async getBookings(): Promise<Booking[]> {
    const response = await apiClient.get<Booking[]>('/bookings');
    return response.data || [];
  }

  // Get all trips for the current user (as guest)
  static async getMyTrips(): Promise<Booking[]> {
    const response = await apiClient.get<Booking[]>('/my-trips');
    return response.data || [];
  }

  // Get a specific booking by ID
  static async getBooking(bookingId: string): Promise<Booking> {
    const response = await apiClient.get<Booking>(`/bookings/${bookingId}`);
    return response.data;
  }

  // Create a new booking
  static async createBooking(bookingData: BookingForm): Promise<Booking> {
    const response = await apiClient.post<Booking>('/bookings', bookingData);
    const booking = response.data;

    // Note: Check-in form generation and booking confirmations are now automated on the backend
    console.log('Booking created successfully. Check-in form and confirmations will be automatically generated.');

    return booking;
  }

  // Update an existing booking
  static async updateBooking(bookingId: string, bookingData: Partial<BookingForm>): Promise<Booking> {
    const response = await apiClient.put<Booking>(`/bookings/${bookingId}`, bookingData);
    return response.data;
  }



  // Cancel a booking with enhanced response handling
  static async cancelBooking(bookingId: string, listingId?: string): Promise<{ message: string; booking: Booking }> {
    const url = listingId ? `/bookings/${bookingId}?listingId=${listingId}` : `/bookings/${bookingId}`;
    const response = await apiClient.put<{ message: string; booking: Booking }>(url, { status: 'cancelled' });
    return response.data;
  }

  // Update booking status (for other status changes)
  static async updateBookingStatus(bookingId: string, status: 'pending' | 'confirmed' | 'completed', listingId?: string): Promise<Booking> {
    const url = listingId ? `/bookings/${bookingId}?listingId=${listingId}` : `/bookings/${bookingId}`;
    const response = await apiClient.put<{ booking: Booking }>(url, { status });
    return response.data.booking || response.data;
  }

  // Update payment status
  static async updatePaymentStatus(bookingId: string, paymentStatus: 'pending' | 'paid' | 'refunded', listingId?: string): Promise<Booking> {
    const url = listingId ? `/bookings/${bookingId}?listingId=${listingId}` : `/bookings/${bookingId}`;
    const response = await apiClient.put<{ booking: Booking }>(url, { paymentStatus });
    return response.data.booking || response.data;
  }

  // Confirm booking (update status to confirmed)
  static async confirmBooking(bookingId: string, listingId?: string): Promise<Booking> {
    return this.updateBookingStatus(bookingId, 'confirmed', listingId);
  }

  // Confirm payment (update payment status to paid)
  static async confirmPayment(bookingId: string, listingId?: string): Promise<Booking> {
    return this.updatePaymentStatus(bookingId, 'paid', listingId);
  }

  // Get bookings by listing ID
  static async getBookingsByListing(listingId: string): Promise<Booking[]> {
    const response = await apiClient.get<Booking[]>(`/listings/${listingId}/bookings`);
    return response.data || [];
  }

  // Check for booking conflicts
  static async checkAvailability(listingId: string, startDate: string, endDate: string): Promise<boolean> {
    const response = await apiClient.post<{ available: boolean }>('/bookings/check-availability', {
      listingId,
      startDate,
      endDate,
    });
    return response.data.available;
  }

  // Helper function to format booking dates
  static formatDateRange(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const options: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: 'numeric',
      year: start.getFullYear() !== end.getFullYear() ? 'numeric' : undefined
    };
    
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  }

  // Helper function to calculate booking duration
  static calculateDuration(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // Get booking confirmation status
  static async getBookingConfirmationStatus(bookingId: string) {
    try {
      return await BookingConfirmationService.getConfirmationStatus(bookingId);
    } catch (err) {
      console.error('Failed to get confirmation status:', err);
      return null;
    }
  }

  // Retry booking confirmation
  static async retryBookingConfirmation(bookingId: string) {
    try {
      return await BookingConfirmationService.retryBookingConfirmation(bookingId);
    } catch (err) {
      console.error('Failed to retry confirmation:', err);
      throw err;
    }
  }

  // Send manual reminder
  static async sendBookingReminder(bookingId: string, customMessage?: string) {
    try {
      return await BookingConfirmationService.sendManualReminder(bookingId, customMessage);
    } catch (err) {
      console.error('Failed to send reminder:', err);
      throw err;
    }
  }

  // Get communication history
  static async getBookingCommunications(bookingId: string) {
    try {
      return await BookingConfirmationService.getCommunicationHistory(bookingId);
    } catch (err) {
      console.error('Failed to get communication history:', err);
      return [];
    }
  }

  // Helper function to get status color
  static getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  }
}

export default BookingsService;